import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import { FeeDisclaimer, CrudPermissions } from '../../types';
import { useData } from '../../contexts/DataContext';

interface FeeDisclaimersManagementProps {
  permissions: CrudPermissions;
}
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const FeeDisclaimersManagement: React.FC<FeeDisclaimersManagementProps> = ({ permissions }) => {
  const { feeDisclaimers: disclaimers, setFeeDisclaimers: setDisclaimers } = useData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDisclaimer, setEditingDisclaimer] = useState<FeeDisclaimer | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<Partial<FeeDisclaimer>>({
    name: '',
    title: '',
    content: '',
    requiresSignature: false,
  });

  const filteredDisclaimers = useMemo(() => {
    return disclaimers.filter((disclaimer) => {
      const matchesSearch =
        disclaimer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        disclaimer.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        disclaimer.content.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesSearch;
    });
  }, [disclaimers, searchQuery]);

  const handleAdd = () => {
    setEditingDisclaimer(null);
    setFormData({
      name: '',
      title: '',
      content: '',
      requiresSignature: false,
    });
    setActiveTab(0);
    setOpenDialog(true);
  };

  const handleEdit = (disclaimer: FeeDisclaimer) => {
    setEditingDisclaimer(disclaimer);
    setFormData(disclaimer);
    setActiveTab(0);
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this disclaimer?')) {
      setDisclaimers(disclaimers.filter((d) => d.id !== id));
      const index = mockFeeDisclaimers.findIndex((d) => d.id === id);
      if (index > -1) {
        mockFeeDisclaimers.splice(index, 1);
      }
    }
  };

  const handleSave = () => {
    if (editingDisclaimer) {
      const updated = disclaimers.map((d) =>
        d.id === editingDisclaimer.id
          ? { ...d, ...formData, updatedAt: new Date() }
          : d
      );
      setDisclaimers(updated);
      const index = mockFeeDisclaimers.findIndex((d) => d.id === editingDisclaimer.id);
      if (index > -1) {
        mockFeeDisclaimers[index] = {
          ...mockFeeDisclaimers[index],
          ...formData,
          updatedAt: new Date(),
        } as FeeDisclaimer;
      }
    } else {
      const newDisclaimer: FeeDisclaimer = {
        id: uuidv4(),
        name: formData.name!,
        title: formData.title!,
        content: formData.content!,
        requiresSignature: formData.requiresSignature!,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      setDisclaimers([...disclaimers, newDisclaimer]);
      mockFeeDisclaimers.push(newDisclaimer);
    }
    setOpenDialog(false);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3,
      }}>
        <Typography variant="h5" fontWeight={600}>
          Fee Disclaimers Management
        </Typography>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Add Disclaimer
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          placeholder="Search disclaimers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {filteredDisclaimers.map((disclaimer) => (
          <Box
            key={disclaimer.id}
            sx={{
              display: 'flex',
              minWidth: 0,
            }}
          >
            <Card
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                    {disclaimer.name}
                  </Typography>
                  {disclaimer.requiresSignature && (
                    <Chip
                      label="Signature Required"
                      size="small"
                      color="warning"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  {disclaimer.title}
                </Typography>
                <Box
                  sx={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    '& h2, & h3': {
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      mt: 0.5,
                      mb: 0.5
                    },
                    '& p': {
                      mb: 0.5,
                      fontSize: '0.875rem'
                    },
                    '& ul, & ol': {
                      mb: 0.5,
                      pl: 2,
                      fontSize: '0.875rem'
                    },
                    '& li': {
                      mb: 0.25,
                      fontSize: '0.875rem'
                    },
                    '& blockquote': {
                      borderLeft: '2px solid #e0e0e0',
                      pl: 1,
                      py: 0.25,
                      my: 0.5,
                      fontSize: '0.875rem'
                    },
                    '& table': {
                      fontSize: '0.75rem',
                      mb: 0.5
                    },
                    '& th, & td': {
                      fontSize: '0.75rem'
                    },
                    '& strong': { fontWeight: 600 }
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {disclaimer.content}
                  </ReactMarkdown>
                </Box>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                {permissions.update && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleEdit(disclaimer)}
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    Edit
                  </Button>
                )}
                {permissions.delete && (
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(disclaimer.id)}
                    color="error"
                    sx={{ flexShrink: 0 }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>

      {filteredDisclaimers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No disclaimers found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? 'Try adjusting your search'
              : 'Click "Add Disclaimer" to create your first disclaimer'}
          </Typography>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDisclaimer ? 'Edit Disclaimer' : 'Add New Disclaimer'}
        </DialogTitle>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tab label="Edit" />
          <Tab label="Preview" disabled={!formData.content} />
        </Tabs>
        <DialogContent dividers sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          {activeTab === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Internal Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
                helperText="For admin reference only, not shown to users"
              />
              <TextField
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                fullWidth
                required
                helperText="This title will be displayed to users"
              />
              <TextField
                label="Content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                fullWidth
                multiline
                rows={12}
                required
                helperText="Supports Markdown formatting (headings, lists, bold, italic, tables, etc.)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requiresSignature}
                    onChange={(e) => setFormData({ ...formData, requiresSignature: e.target.checked })}
                  />
                }
                label="Requires Signature"
              />
            </Box>
          ) : (
            <Box sx={{ py: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                {formData.title || 'Preview'}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  mt: 2,
                  '& h2': { fontSize: '1.5rem', fontWeight: 600, mt: 3, mb: 2 },
                  '& h3': { fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1.5 },
                  '& p': { mb: 1.5, lineHeight: 1.7 },
                  '& ul, & ol': { mb: 2, pl: 3 },
                  '& li': { mb: 0.5 },
                  '& blockquote': {
                    borderLeft: '4px solid #e0e0e0',
                    pl: 2,
                    py: 1,
                    my: 2,
                    fontStyle: 'italic',
                    bgcolor: 'rgba(0, 0, 0, 0.02)'
                  },
                  '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    my: 2
                  },
                  '& th, & td': {
                    border: '1px solid #e0e0e0',
                    p: 1.5,
                    textAlign: 'left'
                  },
                  '& th': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    fontWeight: 600
                  },
                  '& strong': { fontWeight: 600 },
                  '& code': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: '4px',
                    fontFamily: 'monospace'
                  }
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {formData.content || ''}
                </ReactMarkdown>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || !formData.title || !formData.content}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
